import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { OrganizationSettingsInput } from '@/lib/schemas';
import { useAuthStore } from '@/stores/auth-store';
import type { TenantSettings } from '@/types';

interface TenantSettingsResponse {
  tenantId: string;
  settings: TenantSettings | null;
  tenant?: {
    name?: string;
    address?: unknown;
    acn?: string;
  } | null;
}

export interface TenantOrganizationSettings {
  registerName: string;
  address: string;
  acn: string;
}

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function tenantAddressToString(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return '';
  }

  const record = value as Record<string, unknown>;
  const parts = [
    record.formatted,
    record.addressLine1,
    record.addressLine2,
    record.street,
    record.suburb,
    record.city,
    record.state,
    record.postcode,
    record.country,
  ]
    .map(item => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean);

  return parts.filter((part, index) => parts.indexOf(part) === index).join(', ');
}

export function extractTenantOrganizationSettings(
  settings: TenantSettings | null | undefined,
  fallbackRegisterName = '',
  tenant?: TenantSettingsResponse['tenant'],
): TenantOrganizationSettings {
  const brandingConfig = asObject(settings?.brandingConfig);
  const organization = asObject(brandingConfig.organization);

  return {
    registerName:
      asString(organization.registerName) ||
      asString(brandingConfig.registerName) ||
      asString(tenant?.name) ||
      fallbackRegisterName,
    address:
      asString(organization.address) ||
      asString(brandingConfig.address) ||
      tenantAddressToString(tenant?.address),
    acn: asString(organization.acn) || asString(brandingConfig.acn) || asString(tenant?.acn),
  };
}

export function buildTenantOrganizationBrandingConfig(
  existingBrandingConfig: unknown,
  values: OrganizationSettingsInput,
) {
  const brandingConfig = asObject(existingBrandingConfig);
  const currentOrganization = asObject(brandingConfig.organization);

  return {
    ...brandingConfig,
    organization: {
      ...currentOrganization,
      registerName: values.registerName,
      address: values.address ?? '',
      acn: values.acn ?? '',
    },
  };
}

export function useTenantSettings(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: ['tenant-settings', tenantId],
    queryFn: async () => {
      const { data } = await api.get<TenantSettingsResponse>(
        `/tenancy/tenants/${tenantId}/settings`,
      );
      return data;
    },
    enabled: !!tenantId,
  });
}

export function useUpdateTenantOrganizationSettings(tenantId: string | null | undefined) {
  const queryClient = useQueryClient();
  const user = useAuthStore(s => s.user);
  const setUser = useAuthStore(s => s.setUser);

  return useMutation({
    mutationFn: async (values: OrganizationSettingsInput) => {
      const current =
        queryClient.getQueryData<TenantSettingsResponse>(['tenant-settings', tenantId]) ?? null;
      const brandingConfig = buildTenantOrganizationBrandingConfig(
        current?.settings?.brandingConfig,
        values,
      );

      const { data } = await api.patch<TenantSettingsResponse>(
        `/tenancy/tenants/${tenantId}/settings`,
        {
          brandingConfig,
        },
      );
      return data;
    },
    onSuccess: data => {
      queryClient.setQueryData(['tenant-settings', tenantId], data);

      if (!tenantId || !user?.availableTenants?.length) {
        return;
      }

      const nextRegisterName = extractTenantOrganizationSettings(
        data.settings,
        user.availableTenants.find(tenant => tenant.id === tenantId)?.name ?? '',
        data.tenant,
      ).registerName;

      if (!nextRegisterName) {
        return;
      }

      setUser({
        ...user,
        availableTenants: user.availableTenants.map(tenant =>
          tenant.id === tenantId ? { ...tenant, name: nextRegisterName } : tenant,
        ),
      } as any);
    },
  });
}
